/**
 * Cache Configuration Service
 * 
 * Production-safe configuration management for the Phase 4 cache invalidation system.
 * Provides environment-specific settings, feature flags, and runtime configuration.
 * 
 * Phase 5 implementation - production-safe configuration management
 */

import { isDevelopment, isProduction, isTest, getEnvVar } from '@/lib/utils/environment';

/**
 * Cache system feature flags
 */
export interface CacheFeatureFlags {
  enableSelectiveInvalidation: boolean;
  enableAutomaticMigration: boolean;
  enableCrossTabCoordination: boolean;
  enableMonitoring: boolean;
  enableErrorRecovery: boolean;
  enablePerformanceTracking: boolean;
  enableTTLExpiration: boolean;
  enableCommunityAwareness: boolean;
  enableBrandingCacheIntegration: boolean;
  enableDevelopmentResetDetection: boolean;
}

/**
 * Cache system performance configuration
 */
export interface CachePerformanceConfig {
  ttl: {
    default: number;
    communityBranding: number;
    memberOnboarding: number;
    userPreferences: number;
    metaVersion: number;
  };
  invalidation: {
    batchSize: number;
    debounceMs: number;
    maxConcurrentOperations: number;
  };
  monitoring: {
    eventBufferSize: number;
    metricsCollectionInterval: number;
    performanceThresholds: {
      warning: number;
      critical: number;
    };
  };
  recovery: {
    maxRetries: number;
    retryDelays: number[];
    recoveryWindowMs: number;
  };
}

/**
 * Cache system security configuration
 */
export interface CacheSecurityConfig {
  namespacingRequired: boolean;
  communityIsolationRequired: boolean;
  sensitiveKeyPatterns: string[];
  allowedOrigins?: string[];
  encryptionEnabled: boolean;
  sanitizeStoredData: boolean;
}

/**
 * Complete cache configuration
 */
export interface CacheConfiguration {
  environment: 'development' | 'production' | 'test';
  version: string;
  features: CacheFeatureFlags;
  performance: CachePerformanceConfig;
  security: CacheSecurityConfig;
  debugging: {
    enableConsoleLogging: boolean;
    enablePerformanceMarks: boolean;
    enableDetailedErrors: boolean;
  };
}

/**
 * Development configuration
 */
const DEVELOPMENT_CONFIG: CacheConfiguration = {
  environment: 'development',
  version: '4.0.0',
  features: {
    enableSelectiveInvalidation: true,
    enableAutomaticMigration: true,
    enableCrossTabCoordination: true,
    enableMonitoring: true,
    enableErrorRecovery: true,
    enablePerformanceTracking: true,
    enableTTLExpiration: true,
    enableCommunityAwareness: true,
    enableBrandingCacheIntegration: true,
    enableDevelopmentResetDetection: true,
  },
  performance: {
    ttl: {
      default: 24 * 60 * 60 * 1000, // 24 hours
      communityBranding: 60 * 60 * 1000, // 1 hour
      memberOnboarding: 24 * 60 * 60 * 1000, // 24 hours
      userPreferences: 7 * 24 * 60 * 60 * 1000, // 7 days
      metaVersion: 60 * 60 * 1000, // 1 hour
    },
    invalidation: {
      batchSize: 50,
      debounceMs: 100,
      maxConcurrentOperations: 3,
    },
    monitoring: {
      eventBufferSize: 1000,
      metricsCollectionInterval: 5 * 60 * 1000, // 5 minutes
      performanceThresholds: {
        warning: 1000, // 1 second
        critical: 5000, // 5 seconds
      },
    },
    recovery: {
      maxRetries: 3,
      retryDelays: [100, 500, 1000],
      recoveryWindowMs: 60 * 1000, // 1 minute
    },
  },
  security: {
    namespacingRequired: true,
    communityIsolationRequired: true,
    sensitiveKeyPatterns: ['auth', 'session', 'token', 'password'],
    encryptionEnabled: false, // Disabled in dev for easier debugging
    sanitizeStoredData: true,
  },
  debugging: {
    enableConsoleLogging: true,
    enablePerformanceMarks: true,
    enableDetailedErrors: true,
  },
};

/**
 * Production configuration
 */
const PRODUCTION_CONFIG: CacheConfiguration = {
  environment: 'production',
  version: '4.0.0',
  features: {
    enableSelectiveInvalidation: true,
    enableAutomaticMigration: true,
    enableCrossTabCoordination: true,
    enableMonitoring: true,
    enableErrorRecovery: true,
    enablePerformanceTracking: true,
    enableTTLExpiration: true,
    enableCommunityAwareness: true,
    enableBrandingCacheIntegration: true,
    enableDevelopmentResetDetection: false, // Disabled in production
  },
  performance: {
    ttl: {
      default: 7 * 24 * 60 * 60 * 1000, // 7 days
      communityBranding: 4 * 60 * 60 * 1000, // 4 hours
      memberOnboarding: 7 * 24 * 60 * 60 * 1000, // 7 days
      userPreferences: 30 * 24 * 60 * 60 * 1000, // 30 days
      metaVersion: 24 * 60 * 60 * 1000, // 24 hours
    },
    invalidation: {
      batchSize: 25, // Smaller batches in production
      debounceMs: 200, // Longer debounce
      maxConcurrentOperations: 2, // Conservative concurrency
    },
    monitoring: {
      eventBufferSize: 500, // Smaller buffer
      metricsCollectionInterval: 15 * 60 * 1000, // 15 minutes
      performanceThresholds: {
        warning: 2000, // 2 seconds
        critical: 10000, // 10 seconds
      },
    },
    recovery: {
      maxRetries: 2, // Fewer retries in production
      retryDelays: [200, 1000, 3000],
      recoveryWindowMs: 5 * 60 * 1000, // 5 minutes
    },
  },
  security: {
    namespacingRequired: true,
    communityIsolationRequired: true,
    sensitiveKeyPatterns: ['auth', 'session', 'token', 'password', 'secret', 'key'],
    encryptionEnabled: true,
    sanitizeStoredData: true,
    allowedOrigins: getEnvVar('ALLOWED_ORIGINS')?.split(',') || [],
  },
  debugging: {
    enableConsoleLogging: false,
    enablePerformanceMarks: false,
    enableDetailedErrors: false,
  },
};

/**
 * Test configuration
 */
const TEST_CONFIG: CacheConfiguration = {
  environment: 'test',
  version: '4.0.0',
  features: {
    enableSelectiveInvalidation: true,
    enableAutomaticMigration: false, // Disabled for test predictability
    enableCrossTabCoordination: false, // Disabled for test isolation
    enableMonitoring: false, // Disabled for performance
    enableErrorRecovery: true,
    enablePerformanceTracking: false, // Disabled for performance
    enableTTLExpiration: false, // Disabled for test predictability
    enableCommunityAwareness: true,
    enableBrandingCacheIntegration: true,
    enableDevelopmentResetDetection: false,
  },
  performance: {
    ttl: {
      default: 1000, // 1 second for fast tests
      communityBranding: 1000,
      memberOnboarding: 1000,
      userPreferences: 1000,
      metaVersion: 1000,
    },
    invalidation: {
      batchSize: 10,
      debounceMs: 0, // No debouncing in tests
      maxConcurrentOperations: 1, // Sequential operations
    },
    monitoring: {
      eventBufferSize: 100,
      metricsCollectionInterval: 1000, // 1 second
      performanceThresholds: {
        warning: 100,
        critical: 500,
      },
    },
    recovery: {
      maxRetries: 1,
      retryDelays: [10],
      recoveryWindowMs: 1000,
    },
  },
  security: {
    namespacingRequired: true,
    communityIsolationRequired: true,
    sensitiveKeyPatterns: ['auth', 'session'],
    encryptionEnabled: false,
    sanitizeStoredData: false, // Disabled for test data inspection
  },
  debugging: {
    enableConsoleLogging: false, // Disabled to reduce test noise
    enablePerformanceMarks: false,
    enableDetailedErrors: true, // Enabled for test debugging
  },
};

/**
 * Get current configuration based on environment
 */
export function getCacheConfiguration(): CacheConfiguration {
  if (isTest) {
    return { ...TEST_CONFIG };
  }
  
  if (isProduction) {
    return { ...PRODUCTION_CONFIG };
  }
  
  return { ...DEVELOPMENT_CONFIG };
}

/**
 * Runtime configuration overrides
 */
class ConfigurationManager {
  private config: CacheConfiguration;
  private overrides: Partial<CacheConfiguration> = {};

  constructor() {
    this.config = getCacheConfiguration();
  }

  /**
   * Get current configuration with any runtime overrides
   */
  getConfig(): CacheConfiguration {
    return this.mergeConfigurations(this.config, this.overrides);
  }

  /**
   * Set runtime configuration override
   */
  setOverride(path: string, value: any): void {
    this.setNestedProperty(this.overrides, path, value);
  }

  /**
   * Clear all runtime overrides
   */
  clearOverrides(): void {
    this.overrides = {};
  }

  /**
   * Validate configuration
   */
  validateConfig(): { valid: boolean; errors: string[] } {
    const config = this.getConfig();
    const errors: string[] = [];

    // Validate TTL values
    if (config.performance.ttl.default <= 0) {
      errors.push('Default TTL must be positive');
    }

    // Validate performance thresholds
    if (config.performance.monitoring.performanceThresholds.warning >= 
        config.performance.monitoring.performanceThresholds.critical) {
      errors.push('Warning threshold must be less than critical threshold');
    }

    // Validate retry configuration
    if (config.performance.recovery.maxRetries < 0) {
      errors.push('Max retries cannot be negative');
    }

    // Validate security settings
    if (config.security.namespacingRequired && !config.features.enableSelectiveInvalidation) {
      errors.push('Namespacing required but selective invalidation is disabled');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Check if feature is enabled
   */
  isFeatureEnabled(feature: keyof CacheFeatureFlags): boolean {
    return this.getConfig().features[feature];
  }

  /**
   * Get performance setting
   */
  getPerformanceSetting<K extends keyof CachePerformanceConfig>(
    category: K
  ): CachePerformanceConfig[K] {
    return this.getConfig().performance[category];
  }

  /**
   * Get security setting
   */
  getSecuritySetting<K extends keyof CacheSecurityConfig>(
    setting: K
  ): CacheSecurityConfig[K] {
    return this.getConfig().security[setting];
  }

  /**
   * Check if sensitive data should be handled
   */
  isSensitiveKey(key: string): boolean {
    const patterns = this.getSecuritySetting('sensitiveKeyPatterns');
    return patterns.some(pattern => key.toLowerCase().includes(pattern.toLowerCase()));
  }

  /**
   * Merge configurations
   */
  private mergeConfigurations(base: CacheConfiguration, override: Partial<CacheConfiguration>): CacheConfiguration {
    return {
      ...base,
      ...override,
      features: { ...base.features, ...override.features },
      performance: { ...base.performance, ...override.performance },
      security: { ...base.security, ...override.security },
      debugging: { ...base.debugging, ...override.debugging }
    };
  }

  /**
   * Set nested property using dot notation
   */
  private setNestedProperty(obj: any, path: string, value: any): void {
    const keys = path.split('.');
    let current = obj;

    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      if (!(key in current) || typeof current[key] !== 'object') {
        current[key] = {};
      }
      current = current[key];
    }

    current[keys[keys.length - 1]] = value;
  }
}

// Global configuration manager
const configManager = new ConfigurationManager();

/**
 * Get current cache configuration
 */
export function getConfig(): CacheConfiguration {
  return configManager.getConfig();
}

/**
 * Check if a feature is enabled
 */
export function isFeatureEnabled(feature: keyof CacheFeatureFlags): boolean {
  return configManager.isFeatureEnabled(feature);
}

/**
 * Get performance configuration
 */
export function getPerformanceConfig<K extends keyof CachePerformanceConfig>(
  category: K
): CachePerformanceConfig[K] {
  return configManager.getPerformanceSetting(category);
}

/**
 * Get security configuration
 */
export function getSecurityConfig<K extends keyof CacheSecurityConfig>(
  setting: K
): CacheSecurityConfig[K] {
  return configManager.getSecuritySetting(setting);
}

/**
 * Check if a key contains sensitive data
 */
export function isSensitiveKey(key: string): boolean {
  return configManager.isSensitiveKey(key);
}

/**
 * Validate current configuration
 */
export function validateConfiguration(): { valid: boolean; errors: string[] } {
  return configManager.validateConfig();
}

/**
 * Development utilities
 */
export const devConfigUtils = isDevelopment ? {
  /**
   * Override configuration at runtime
   */
  setConfigOverride(path: string, value: any): void {
    configManager.setOverride(path, value);
    console.log(`CacheConfig: Override set for ${path}:`, value);
  },

  /**
   * Clear all configuration overrides
   */
  clearConfigOverrides(): void {
    configManager.clearOverrides();
    console.log('CacheConfig: All overrides cleared');
  },

  /**
   * Get current configuration for debugging
   */
  debugConfig(): void {
    console.log('CacheConfig: Current configuration:', configManager.getConfig());
    console.log('CacheConfig: Validation:', configManager.validateConfig());
  },

  /**
   * Test configuration switching
   */
  testConfigSwitching(): void {
    console.log('Testing configuration switching...');
    
    // Test feature toggle
    configManager.setOverride('features.enableMonitoring', false);
    console.log('Monitoring disabled:', !configManager.isFeatureEnabled('enableMonitoring'));
    
    // Test performance override
    configManager.setOverride('performance.ttl.default', 1000);
    console.log('TTL overridden:', configManager.getPerformanceSetting('ttl').default === 1000);
    
    configManager.clearOverrides();
    console.log('Overrides cleared');
  }
} : {};

// Initialize and validate configuration
const validation = validateConfiguration();
if (!validation.valid) {
  console.error('CacheConfig: Configuration validation failed:', validation.errors);
  
  if (isDevelopment) {
    console.warn('CacheConfig: Continuing with invalid configuration in development');
  }
}